import type { MailThreadView } from "@kwp/contracts";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState, type RefObject } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { useToast } from "../../ui/toast";
import type { ComposeRequest } from "./Composer";
import { threadOf, type ActivityItem, type Entry } from "./activity-model";
import { cursorAfterRemoval, moveCursor, nextAfterRemoval, type Command } from "./keyboard";
import type { Thread } from "./model";
import { returnAction, type Box } from "./mailboxes";
import type { InboxSearch } from "./scope";
import { targetOf, type ThreadCommand } from "./ThreadView";
import type { Inbox } from "./use-inbox";
import type { ActTarget, MailAction } from "./use-mail-actions";

export type DialogState =
  | { kind: "compose"; request: ComposeRequest }
  | { kind: "tag" | "move" | "snooze"; target: ActTarget }
  | { kind: "share"; workspaceId: string; threadId: string; subject: string }
  | { kind: "help" }
  | null;

type After = "reply" | "forward" | null;

type MailTarget = { act: ActTarget; row: Thread | undefined; subject: string; workspaceId: string };

/** A command on one conversation: a mail action, a dialog for one, or a task from it. */
function mailCommand(
  command: Command | ThreadCommand,
  t: MailTarget,
  deps: {
    mutate: (action: MailAction, t: ActTarget) => void;
    setDialog: (dialog: DialogState) => void;
    createTask: (
      input: { workspaceId: string; title: string; source: { kind: "email"; ref: string } },
      handlers: { onSuccess: () => void; onError: (e: unknown) => void },
    ) => void;
    toast: ReturnType<typeof useToast>;
    box: Box;
  },
): void {
  const { mutate, setDialog, createTask, toast, box } = deps;
  switch (command) {
    case "return": {
      // Back to the inbox from Archived, Spam or Trash; elsewhere there is nowhere to return from.
      const back = returnAction(box);
      return back === null ? undefined : mutate({ kind: back.kind }, t.act);
    }
    case "archive":
    case "trash":
    case "spam":
    case "unread":
      return mutate({ kind: command }, t.act);
    case "unsnooze":
      return mutate({ kind: "unsnooze" }, t.act);
    case "star":
      return mutate({ kind: t.row?.starred === true ? "unstar" : "star" }, t.act);
    case "move":
    case "tag":
    case "snooze":
      return setDialog({ kind: command, target: t.act });
    case "share":
      return setDialog({ kind: "share", workspaceId: t.workspaceId, threadId: t.act.id, subject: t.subject });
    case "task":
      return createTask(
        { workspaceId: t.workspaceId, title: t.subject.trim() === "" ? "Follow up on an email" : t.subject.trim().slice(0, 300), source: { kind: "email", ref: t.act.id } },
        {
          onSuccess: () => toast({ message: "Task created.", action: { label: "Open Tasks", href: `/w/${t.workspaceId}/tasks` } }),
          onError: (e) => toast({ tone: "error", message: errorMessage(e) }),
        },
      );
  }
}

/**
 * Whether a conversation stays in this mailbox after the action, so there is nothing to hide:
 * snoozed threads are already archived; a sent one is still sent when archived, snoozed or woken
 * (only trash or spam moves its sent mail); archiving in Archived, and so on.
 */
export function staysIn(box: Box, kind: MailAction["kind"]): boolean {
  switch (box) {
    case "snoozed":
      return kind === "archive" || kind === "snooze";
    case "sent":
      return kind === "archive" || kind === "snooze" || kind === "unsnooze";
    case "archive":
      return kind === "archive";
    case "spam":
      return kind === "spam";
    case "trash":
      return kind === "trash";
    case "inbox":
      return false;
  }
}

/**
 * What each keyboard command and toolbar button does, in one place, so a key and a click can
 * never disagree. In the list a command acts on the cursor row; in a conversation, on it.
 */
export function useInboxCommands(opts: {
  workspaceId: string;
  inbox: Inbox;
  search: InboxSearch;
  onSearch: (patch: Partial<InboxSearch>) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  /** `x`: toggles the selection of the cursor row. */
  onSelectRow: (index: number) => void;
}) {
  const { workspaceId, inbox, search, onSearch, searchRef, onSelectRow } = opts;
  const trpc = useTRPC();
  const toast = useToast();
  const createTask = useMutation(trpc.tasks.create.mutationOptions());
  const onActivityError = (e: unknown) => toast({ tone: "error", message: errorMessage(e) });
  const markRead = useMutation(trpc.activity.markRead.mutationOptions({ onError: onActivityError }));
  const done = useMutation(trpc.activity.done.mutationOptions({ onError: onActivityError }));
  const [cursor, setCursor] = useState(0);
  const [focusTick, setFocusTick] = useState(0);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [after, setAfter] = useState<After>(null);
  const { entries, actions } = inbox;
  const view: MailThreadView | null = inbox.open.data === undefined ? null : inbox.open.data;
  // A conversation or a chat or task row is open (the right pane, or the whole screen on a phone).
  const inItem = search.a !== undefined;
  const inThread = search.t !== undefined || inItem;
  const cursorThread = threadOf(entries[cursor]);

  const moveTo = useCallback((index: number) => {
    setCursor(index);
    setFocusTick((n) => n + 1);
  }, []);

  const openThread = useCallback(
    (t: Thread | { id: string }, then: After = null) => {
      setAfter(then);
      onSearch({ t: t.id, a: undefined });
      if ("unread" in t && t.unread) void actions.act(t, { kind: "read" });
    },
    [onSearch, actions],
  );

  /** A chat or task row: opened beside the list, and read (a chat's read marker follows in Chat). */
  const { mutate: markItemRead } = markRead;
  const { unread } = inbox.activity;
  const openActivity = useCallback(
    (item: ActivityItem) => {
      setAfter(null);
      onSearch({ a: item.id, t: undefined });
      if (unread(item)) markItemRead({ activityId: item.id });
    },
    [onSearch, unread, markItemRead],
  );

  const openEntry = useCallback((entry: Entry) => (entry.kind === "thread" ? openThread(entry.thread) : openActivity(entry.item)), [openThread, openActivity]);

  const back = useCallback(() => {
    setAfter(null);
    onSearch({ t: undefined, a: undefined });
    setFocusTick((n) => n + 1);
  }, [onSearch]);

  /** The open conversation, chat or task left the list: open the next one, or back to the list. */
  const advance = useCallback(
    (id: string) => {
      const next = nextAfterRemoval(entries, id);
      if (next === null) return back();
      setCursor(next.index);
      openEntry(next.entry);
    },
    [entries, back, openEntry],
  );

  /** A thread's workspace is its account's (threads do not carry one, §8); the page's if unknown. */
  const { accounts } = inbox;
  const workspaceOf = useCallback(
    (row: Thread): string => {
      const account = accounts.find((a) => a.id === row.accountId);
      return account === undefined ? workspaceId : account.workspaceId;
    },
    [accounts, workspaceId],
  );

  /** The thread a command acts on, or null when there is none. */
  const target = useCallback((): { act: ActTarget; row: Thread | undefined; subject: string; workspaceId: string } | null => {
    if (inItem) return null;
    if (inThread) {
      const row = inbox.row;
      if (row !== undefined) return { act: row, row, subject: row.subject, workspaceId: workspaceOf(row) };
      if (view === null) return null;
      return { act: targetOf(view, []), row: undefined, subject: view.subject, workspaceId };
    }
    const row = cursorThread;
    return row === undefined ? null : { act: row, row, subject: row.subject, workspaceId: workspaceOf(row) };
  }, [inItem, inThread, inbox.row, view, cursorThread, workspaceId, workspaceOf]);

  const mutate = useCallback(
    (action: MailAction, t: ActTarget) => {
      const stays = staysIn(inbox.box, action.kind);
      const leaves = !stays && ["archive", "trash", "spam", "moveToFolder", "unread", "snooze", "unsnooze", "unarchive", "notSpam", "untrash"].includes(action.kind);
      // Out of the list from an open conversation: the next one opens, so Delete, Delete... works
      // down the inbox. Marking unread is "read it later": back to the list instead.
      if (inThread && leaves && action.kind !== "unread") advance(t.id);
      else if (inThread && (leaves || stays)) back();
      else if (!inThread && leaves && action.kind !== "unread") setCursor((c) => cursorAfterRemoval(c, entries.length - 1));
      void actions.act(t, action, { stays });
    },
    [inThread, advance, back, actions, entries.length, inbox.box],
  );

  /** Done on a chat or task row: out of the inbox (it stays in Chat or Tasks). */
  const { mutate: markDone } = done;
  const finish = useCallback(
    (item: ActivityItem) => {
      if (search.a === item.id) advance(item.id);
      else setCursor((c) => cursorAfterRemoval(c, entries.length - 1));
      markDone({ activityId: item.id });
    },
    [search.a, advance, entries.length, markDone],
  );

  const compose = useCallback(
    (mode: "reply" | "forward") => {
      const last = view === null ? undefined : view.messages[view.messages.length - 1];
      if (inThread && last !== undefined && view !== null) {
        setDialog({ kind: "compose", request: mode === "reply" ? { mode, message: last, accountAddress: view.accountAddress } : { mode, message: last } });
        return;
      }
      const entry = entries[cursor];
      if (inThread || entry === undefined) return;
      if (entry.kind === "thread") openThread(entry.thread, mode);
      else openActivity(entry.item);
    },
    [view, inThread, entries, cursor, openThread, openActivity],
  );

  const run = useCallback(
    (command: Command | ThreadCommand) => {
      if (command === "compose") return setDialog({ kind: "compose", request: { mode: "new" } });
      if (command === "help") return setDialog({ kind: "help" });
      if (command === "search") return searchRef.current?.focus();
      if (command === "back") return back();
      if (command === "next" || command === "prev") {
        const delta = command === "next" ? 1 : -1;
        if (!inThread) return moveTo(moveCursor(cursor, delta, entries.length));
        const current = search.a === undefined ? search.t : search.a;
        const at = entries.findIndex((e) => e.id === current);
        const next = entries[moveCursor(at, delta, entries.length)];
        if (next !== undefined && next.id !== current) {
          setCursor(entries.indexOf(next));
          openEntry(next);
        }
        return undefined;
      }
      if (command === "open") {
        const entry = entries[cursor];
        return entry === undefined ? undefined : openEntry(entry);
      }
      // A chat or task row (open, or under the cursor): Done is its one keyboard action.
      const item = inItem ? inbox.openItem : inThread ? undefined : entries[cursor]?.kind === "activity" ? (entries[cursor] as Extract<Entry, { kind: "activity" }>).item : undefined;
      if (item !== undefined || inItem) return command === "archive" && item !== undefined ? finish(item) : undefined;
      if (command === "select") return inThread ? undefined : onSelectRow(cursor);
      if (command === "reply" || command === "forward") return compose(command);
      const t = target();
      if (t === null) return undefined;
      return mailCommand(command, t, { mutate, setDialog, createTask: createTask.mutate, toast, box: inbox.box });
    },
    [searchRef, back, inThread, inItem, moveTo, cursor, entries, search.t, search.a, openEntry, compose, target, mutate, finish, inbox.openItem, inbox.box, createTask, toast, onSelectRow],
  );

  return { run, cursor, setCursor, focusTick, dialog, setDialog, after, setAfter, openThread, openActivity, openEntry, finish, back, mutate };
}
